/**
 * One step on the canvas — the concept's `flow/Node.jsx`, typed, in Tailwind,
 * drawing the shared contract's nodes.
 *
 * ## TWO SHAPES, AS IN THE REFERENCE
 *
 * Split, wait and condition are round gradient logic nodes with their label
 * under them; everything else is a card. A WhatsApp card is the green bubble
 * of its approved template with one row per quick-reply button — and each row
 * is its own port, because a tap on "Not now" and a tap on "Show me" are
 * different journeys. "No reply in 24h" is the last row, dashed.
 *
 * ## PORTS
 *
 * A connected port is a filled dot; a free one, in edit mode, is a real
 * `<button>` with a "+" that opens the add menu, named for the path it adds
 * after ("Add a step after “Not now”"). The holdout port is a lock and never
 * a "+": people held out get nothing, so nothing may follow them. Every port
 * carries `data-port="node:port"` so the editor can measure where edges start.
 *
 * ## NUMBERS IN RUN MODE ARE THE API'S
 *
 * `stat` is one row of `GET /flows/:id/stats`. A port with no row in
 * `stat.ports` shows 0 only because the stats contract counts every port that
 * people left by; nothing here estimates. A message's delivered / read /
 * replied are drawn only when the API measured them (non-null), never as 0.
 */
import {
  FLOW_HOLDOUT_PORT,
  describeTrigger,
  portsOf,
  type FlowNode,
  type FlowNodeStats,
  type FlowPort,
  type FlowSettings,
} from '@wend/shared';
import { cn } from '@wend/ui';
import { memo, type CSSProperties, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';

import {
  IcChat,
  IcCheck,
  IcClock,
  IcClose,
  IcComment,
  IcCond,
  IcExit,
  IcFlag,
  IcLock,
  IcMail,
  IcPerson,
  IcSpark,
  IcSplit,
  IcStart,
} from './icons';
import { CARD_W, ROUND_W, isRound } from './layout';
import {
  conditionLabel,
  goalLabel,
  n,
  nodeName,
  splitSum,
  updateLabel,
  waitLabel,
  type FlowEditorData,
} from './model';

export type FlowMode = 'edit' | 'test' | 'run';
export type TestMark = 'current' | 'path' | 'off' | null;

/* -------------------------------------------------------------------------- */
/* Bits                                                                        */
/* -------------------------------------------------------------------------- */

const RE_VAR = /(\{\{\d+\}\})/;
const RE_ONE = /^\{\{(\d+)\}\}$/;

/** Template text with each `{{n}}` drawn as a chip naming what fills it. */
export function TemplateText({ text, names }: { text: string; names?: ReadonlyMap<number, string> }) {
  return (
    <>
      {text.split(RE_VAR).map((part, i) => {
        const match = RE_ONE.exec(part);
        if (!match) return <span key={i}>{part}</span>;
        const position = Number(match[1]);
        return (
          <span key={i} className="whitespace-nowrap rounded-[5px] bg-brand-b/28 px-[5px] font-mono text-[11.5px] text-brand-ink-strong">
            {names?.get(position) ?? part.slice(2, -2)}
          </span>
        );
      })}
    </>
  );
}

const TYPE_ICON = {
  trigger: IcStart,
  split: IcSplit,
  wait: IcClock,
  condition: IcCond,
  update: IcPerson,
  goal: IcFlag,
  exit: IcExit,
  comment: IcComment,
} as const;

export function NodeIcon({ node, size }: { node: FlowNode; size: number }) {
  if (node.type === 'message') return node.data.channel === 'email' ? <IcMail size={size} /> : <IcChat size={size} />;
  const Icon = TYPE_ICON[node.type];
  return <Icon size={size} />;
}

/** The head's colour per type — the reference's `fe-t-*` / `fe-ch-*`. */
export function nodeTone(node: FlowNode): string {
  switch (node.type) {
    case 'trigger':
      return 'text-info';
    case 'message':
      return node.data.channel === 'email' ? 'text-[#8EC5FF]' : 'text-good';
    case 'update':
      return 'text-brand-ink';
    case 'goal':
      return 'text-good';
    case 'exit':
      return 'text-bad';
    case 'comment':
      return 'text-warn';
    default:
      return 'text-white';
  }
}

export const REENTRY_TEXT = {
  never: 'once ever',
  after_days: (days: number | null) => `again after ${days ?? '?'} days`,
  always: 'every time they qualify',
} as const;

export function reentryText(settings: FlowSettings): string {
  return settings.reentry === 'after_days' ? REENTRY_TEXT.after_days(settings.reentryDays) : REENTRY_TEXT[settings.reentry];
}

interface PortProps {
  nodeId: string;
  port: FlowPort;
  connected: boolean;
  editable: boolean;
  menuOpen: boolean;
  onAdd: (nodeId: string, portId: string) => void;
  /** `row`: the port sits on a row inside a padded card; `edge`: on the node's own edge. */
  at: 'row' | 'edge';
}

/** One output port. See the header. */
function Port({ nodeId, port, connected, editable, menuOpen, onAdd, at }: PortProps) {
  const key = `${nodeId}:${port.id}`;
  const style: CSSProperties = { left: at === 'row' ? 'calc(100% + 15px)' : 'calc(100% + 1px)' };
  const base = 'absolute top-1/2 z-[3] grid -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full p-0';
  if (port.id === FLOW_HOLDOUT_PORT) {
    return (
      <span className={cn(base, 'size-5 border border-dashed border-white/30 bg-surface-1 text-muted')} style={style} data-port={key} title="Holdout — gets no messages">
        <IcLock size={11} />
      </span>
    );
  }
  if (connected || !editable) {
    return (
      <span
        aria-hidden="true"
        data-port={key}
        style={style}
        className={cn(base, 'size-3', connected ? 'bg-white shadow-[0_0_0_3px_var(--color-surface-1)]' : 'border-[1.5px] border-white/35 bg-surface-1')}
      />
    );
  }
  return (
    <button
      type="button"
      data-port={key}
      data-addport={key}
      style={style}
      aria-label={`Add a step after “${port.label}”`}
      aria-haspopup="menu"
      aria-expanded={menuOpen}
      onClick={(event) => {
        event.stopPropagation();
        onAdd(nodeId, port.id);
      }}
      className={cn(
        base,
        'size-5 cursor-pointer border-[1.5px] border-white/50 bg-surface-1 text-[15px] font-semibold leading-none text-white transition',
        "before:absolute before:-inset-1.5 before:content-['']",
        'hover:bg-white hover:text-[#120E28] hover:shadow-[0_0_0_5px_rgba(155,94,246,.38)]',
        'focus-visible:bg-white focus-visible:text-[#120E28] focus-visible:shadow-[0_0_0_5px_rgba(155,94,246,.38)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-b',
        menuOpen && 'bg-white text-[#120E28] shadow-[0_0_0_5px_rgba(155,94,246,.38)]',
      )}
    >
      <span aria-hidden="true" className="-mt-0.5 block">+</span>
    </button>
  );
}

function Count({ value }: { value: number | undefined }) {
  if (value === undefined) return null;
  return <span className="absolute right-[22px] text-[11.5px] font-semibold tabular-nums text-brand-ink">{n(value)}</span>;
}

function CardStats({ stat, terminal }: { stat: FlowNodeStats; terminal: boolean }) {
  return (
    <div className="mt-2.5 flex flex-wrap gap-x-2.5 gap-y-1 border-t border-line pt-2 text-[11.5px] text-muted">
      <span><b className="mr-0.5 font-semibold tabular-nums text-white">{n(stat.entered)}</b> {terminal ? 'reached' : 'entered'}</span>
      {!terminal && <span><b className="mr-0.5 font-semibold tabular-nums text-white">{n(stat.waitingNow)}</b> in step</span>}
      <span className="text-good"><b className="mr-0.5 font-semibold tabular-nums">{n(stat.converted)}</b> converted</span>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* The node                                                                    */
/* -------------------------------------------------------------------------- */

export interface NodeViewProps {
  node: FlowNode;
  mode: FlowMode;
  data: FlowEditorData;
  settings: FlowSettings;
  connected: ReadonlySet<string>;
  selected: boolean;
  /** The port whose add menu is open on this node, if any. */
  menuPort: string | null;
  test: TestMark;
  stat: FlowNodeStats | null;
  /** A problem the editor found on this step (preflight or autosave), drawn as an amber ring. */
  flagged: boolean;
  onSelect: (id: string) => void;
  onAdd: (nodeId: string, portId: string) => void;
  onAccept: (id: string) => void;
  onReject: (id: string) => void;
  onDragStart?: (id: string, event: ReactPointerEvent<HTMLDivElement>) => void;
}

function NodeViewInner({
  node,
  mode,
  data,
  settings,
  connected,
  selected,
  menuPort,
  test,
  stat,
  flagged,
  onSelect,
  onAdd,
  onAccept,
  onReject,
  onDragStart,
}: NodeViewProps) {
  const editable = mode === 'edit';
  const sug = node.suggested !== undefined;
  const shape = node.type === 'message' && node.data.channel === 'whatsapp' && node.data.templateId ? data.waShapes.get(node.data.templateId) : undefined;
  const ports = portsOf(node, shape ?? null);
  const round = isRound(node.type);
  const name = nodeName(node);

  const port = (p: FlowPort | undefined, at: 'row' | 'edge'): ReactNode =>
    p ? (
      <Port
        key={p.id}
        nodeId={node.id}
        port={p}
        connected={connected.has(p.id)}
        editable={editable && !sug}
        menuOpen={menuPort === p.id}
        onAdd={onAdd}
        at={at}
      />
    ) : null;
  const runCount = (pid: string) => (stat ? <Count value={stat.ports[pid] ?? 0} /> : null);
  const select = () => onSelect(node.id);

  const stateCls = cn(
    test === 'off' && 'opacity-60',
    flagged && 'ring-2 ring-warn/60 ring-offset-2 ring-offset-surface-1',
  );

  const sugBar = sug ? (
    <div className={cn('absolute bottom-[calc(100%+9px)] flex min-w-[200px] items-center justify-between gap-2', round ? '-left-[30px] -right-[30px]' : 'inset-x-0')}>
      <span className="inline-flex items-center gap-1.5 rounded-full bg-[linear-gradient(135deg,#7B4DFF,#9B5EF6)] px-3 py-[5px] text-[12.5px] font-semibold text-white shadow-[0_8px_20px_-8px_rgba(123,77,255,.9)]">
        <IcSpark size={14} /> Suggested
      </span>
      {editable && (
        <span className="flex gap-1.5" data-noclick="">
          <button
            type="button"
            data-sugok=""
            aria-label="Accept suggested step"
            onClick={(event) => {
              event.stopPropagation();
              onAccept(node.id);
            }}
            className="relative grid size-7 cursor-pointer place-items-center rounded-full bg-brand-b text-white before:absolute before:-inset-1.5 before:content-[''] hover:bg-[#B07CFF] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-b"
          >
            <IcCheck size={14} />
          </button>
          <button
            type="button"
            aria-label="Reject suggested step"
            onClick={(event) => {
              event.stopPropagation();
              onReject(node.id);
            }}
            className="relative grid size-7 cursor-pointer place-items-center rounded-full bg-white text-[#120E28] before:absolute before:-inset-1.5 before:content-[''] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-b"
          >
            <IcClose size={13} />
          </button>
        </span>
      )}
    </div>
  ) : null;

  const reason = sug ? (
    <p className={cn('mb-2 flex items-start gap-1.5 text-[12px] leading-snug text-brand-ink', round && '-ml-[30px] mt-2 w-[220px] text-left')}>
      <IcSpark size={12} className="mt-0.5 flex-none" /> {node.suggested?.reason}
    </p>
  ) : null;

  const rootHandlers = {
    'data-node': node.id,
    onPointerDown: onDragStart ? (event: ReactPointerEvent<HTMLDivElement>) => onDragStart(node.id, event) : undefined,
    onClick: (event: ReactMouseEvent) => {
      event.stopPropagation();
      if (mode === 'test') select();
    },
  };

  /* ---- round logic nodes -------------------------------------------------- */
  if (round) {
    let summary = '';
    let below: ReactNode = null;
    if (node.type === 'split') {
      const sum = splitSum(node);
      const rows = [
        ...node.data.rows.map((row) => ({ id: row.id, label: row.label, pct: row.pct, hold: false })),
        ...(node.data.holdout ? [{ id: FLOW_HOLDOUT_PORT, label: 'Holdout', pct: node.data.holdout.pct, hold: true }] : []),
      ];
      summary = rows.map((row) => `${row.label} ${row.pct}%`).join(', ');
      below = (
        <>
          <div className={cn('relative mx-auto mt-2 w-32 rounded-[10px] border bg-surface-2 text-center', sum !== 100 ? 'border-bad/60' : 'border-line-strong', stat && 'w-[150px]')}>
            {rows.map((row, i) => (
              <div
                key={row.id}
                className={cn(
                  'relative flex h-9 items-center justify-center gap-1.5 text-[13px] text-white',
                  i > 0 && 'border-t border-line',
                  row.hold && 'bg-[repeating-linear-gradient(135deg,rgba(255,255,255,.025)_0_6px,transparent_6px_12px)]',
                  stat && 'justify-start pl-3',
                )}
              >
                <span aria-hidden="true" className="pointer-events-none absolute inset-y-0 left-0 bg-brand-b/16" style={{ width: `${Math.min(100, row.pct)}%` }} />
                <b className={cn('relative font-semibold tabular-nums', row.hold && 'text-muted')}>{row.pct}%</b>
                <small className="relative text-[11px] text-muted">{row.label}</small>
                {runCount(row.id)}
                {port(ports.find((p) => p.id === row.id), 'edge')}
              </div>
            ))}
          </div>
          {sum !== 100 && (
            <div className="mt-1.5 text-[11.5px] text-bad" role="status">
              Adds up to {sum}%
            </div>
          )}
        </>
      );
    } else if (node.type === 'wait') {
      const label = waitLabel(node.data);
      summary = `${label.pre} ${label.main}`;
      below = (
        <div className="relative mx-auto mt-2 w-max max-w-[160px] whitespace-nowrap rounded-[10px] border border-line-strong bg-surface-2 px-3 py-[7px] text-[12.5px] text-muted">
          {label.pre === 'For' ? '' : `${label.pre} `}
          <b className="font-semibold text-white">{label.main}</b>
          {port(ports[0], 'edge')}
        </div>
      );
    } else if (node.type === 'condition') {
      summary = conditionLabel(node.data, data.fields);
      below = (
        <div className="relative mx-auto mt-2 w-32 rounded-[10px] border border-line-strong bg-surface-2 text-center">
          {ports.map((p, i) => (
            <div key={p.id} className={cn('relative flex h-9 items-center justify-center gap-1.5 text-[13px]', i > 0 && 'border-t border-line')}>
              <b className={cn('font-semibold', p.id === 'yes' ? 'text-good' : 'text-bad')}>{p.label}</b>
              {runCount(p.id)}
              {port(p, 'edge')}
            </div>
          ))}
        </div>
      );
    }
    return (
      <div
        {...rootHandlers}
        className={cn('absolute z-[2] cursor-pointer text-center', stateCls)}
        style={{ left: node.position.x, top: node.position.y, width: ROUND_W }}
      >
        {sugBar}
        <span data-in={node.id} className="pointer-events-none absolute left-[53px] top-[27px] size-0.5" />
        <button
          type="button"
          data-head=""
          onClick={(event) => {
            event.stopPropagation();
            select();
          }}
          aria-label={`${name}: ${summary}. ${editable ? 'Configure' : 'Details'}`}
          aria-pressed={selected}
          className={cn(
            'mx-auto grid size-[54px] cursor-pointer place-items-center rounded-full border-0 bg-[linear-gradient(135deg,#6D5EF6,#9B5EF6)] text-white',
            'shadow-[0_12px_26px_-10px_rgba(109,94,246,.9),inset_0_1px_0_rgba(255,255,255,.28)] transition-shadow',
            'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-b',
            selected && 'shadow-[0_0_0_4px_rgba(155,94,246,.4),0_12px_26px_-10px_rgba(109,94,246,.9)]',
            test === 'path' && 'shadow-[0_0_0_4px_rgba(155,94,246,.3)]',
            test === 'current' && 'shadow-[0_0_0_6px_rgba(155,94,246,.45),0_0_30px_rgba(155,94,246,.5)] motion-safe:animate-pulse',
            sug && 'outline-[1.5px] outline-offset-4 outline-dashed outline-[#B08CFF]',
          )}
        >
          <NodeIcon node={node} size={22} />
        </button>
        <div className="mt-1.5 text-[12.5px] text-muted">
          {node.type === 'condition' ? <span className="block text-[13px] font-medium leading-tight text-white">{summary}</span> : name}
        </div>
        {below}
        {reason}
        {stat && (
          <div className="mt-1.5 text-[11.5px] font-semibold tabular-nums text-muted" aria-live="polite">
            {node.type === 'wait' ? `${n(stat.waitingNow)} waiting · ${n(stat.entered)} entered` : `${n(stat.entered)} ${node.type === 'split' ? 'split' : 'checked'}`}
          </div>
        )}
      </div>
    );
  }

  /* ---- cards -------------------------------------------------------------- */
  let body: ReactNode = null;
  let summary = '';
  let headExtra: ReactNode = null;

  if (node.type === 'trigger') {
    const trigger = settings.trigger;
    const segId = trigger.type === 'segment_entry' ? trigger.segmentId : settings.audienceSegmentId;
    const seg = segId ? data.segments.find((s) => s.id === segId) : undefined;
    const kicker =
      trigger.type === 'segment_entry'
        ? 'When someone enters segment'
        : trigger.type === 'schedule'
          ? 'On a schedule'
          : trigger.type === 'event'
            ? 'When something happens'
            : 'When you enrol people';
    summary = describeTrigger(trigger, seg?.name ?? null);
    body = (
      <>
        <div className="font-mono text-[10.5px] uppercase tracking-[0.11em] text-muted">{kicker}</div>
        <div className="mb-2 mt-1 text-[14.5px] font-semibold leading-tight text-white">
          {trigger.type === 'segment_entry' ? (seg?.name ?? 'Pick a segment') : summary}
        </div>
        {(seg || trigger.type === 'schedule') && (
          <div className="flex flex-wrap gap-1.5">
            {seg && (
              <span className={cn('inline-flex items-center rounded-full border px-2 py-0.5 font-mono text-[10.5px]', seg.type === 'rule' ? 'border-brand-b/45 bg-brand-a/20 text-brand-ink' : 'border-info/40 bg-info/12 text-info')}>
                {seg.type === 'rule' ? 'dynamic' : seg.type}
              </span>
            )}
            {seg && <span className="inline-flex items-center rounded-full border border-line-strong bg-white/[0.06] px-2 py-0.5 font-mono text-[10.5px] text-muted">{n(seg.counts.uniqueRecipients)} now</span>}
            {trigger.type === 'schedule' && seg && <span className="inline-flex items-center rounded-full border border-line-strong bg-white/[0.06] px-2 py-0.5 font-mono text-[10.5px] text-muted">{seg.name}</span>}
          </div>
        )}
        <div className="mt-2 text-[11.5px] leading-snug text-muted">Re-entry {reentryText(settings)}</div>
        {port(ports[0], 'edge')}
      </>
    );
  } else if (node.type === 'message' && node.data.channel === 'email') {
    const tpl = node.data.templateId ? data.templates.find((t) => t.id === node.data.templateId) : undefined;
    summary = tpl ? `Email ${tpl.name}` : 'Email — no template chosen';
    if (tpl) headExtra = <OkDot />;
    body = (
      <>
        <div className="mb-0.5 mt-2 rounded-[10px] border border-info/28 bg-info/7 px-3 py-2.5">
          <div className="mb-1 text-[13px] font-semibold text-white">{tpl ? (tpl.subject ?? '(no subject)') : 'Choose an approved email template'}</div>
          <div className="text-[12.5px] leading-snug text-muted">{tpl ? tpl.name : 'Only approved templates are offered.'}</div>
        </div>
        {stat?.messages && (
          <div className="mt-2 text-[11.5px] text-muted">
            {n(stat.messages.sent)} sent{stat.messages.delivered !== null ? ` · ${n(stat.messages.delivered)} delivered` : ''} · {n(stat.messages.skipped)} skipped
          </div>
        )}
        {port(ports[0], 'edge')}
      </>
    );
  } else if (node.type === 'message') {
    const tpl = node.data.templateId ? data.templates.find((t) => t.id === node.data.templateId) : undefined;
    const vars = node.data.channel === 'whatsapp' ? node.data.vars : [];
    const names = new Map<number, string>(vars.map((binding) => [binding.position, binding.source.replace(/^extras\./, '')]));
    summary = tpl ? `WhatsApp ${tpl.name}, approved` : 'WhatsApp — no template chosen';
    if (tpl) headExtra = <OkDot />;
    body = (
      <>
        <div className="mb-2.5 mt-2 rounded-[4px_12px_12px_12px] border border-good/32 bg-good/14 px-3 py-2.5 text-[13px] leading-normal text-[#E6FFF2]">
          {!tpl ? (
            'Choose an approved WhatsApp template.'
          ) : shape ? (
            <>
              {shape.header && <b className="mb-1 block">{shape.header}</b>}
              <TemplateText text={shape.body} names={names} />
            </>
          ) : (
            <span className="text-good/80">{tpl.name}</span>
          )}
        </div>
        {ports.length === 1 && ports[0]?.id === 'out' ? (
          <div className="relative mt-1.5 flex min-h-[34px] items-center justify-center rounded-lg border border-dashed border-white/16 px-2.5 py-1.5 text-[12.5px] text-muted">
            {ports[0].label}
            {runCount('out')}
            {port(ports[0], 'row')}
          </div>
        ) : (
          ports.map((p) => (
            <div
              key={p.id}
              className={cn(
                'relative mt-[7px] flex min-h-[34px] items-center justify-center gap-1.5 rounded-lg border px-2.5 py-[7px] text-[13px]',
                p.kind === 'noreply' ? 'border-dashed border-white/16 bg-transparent text-[12.5px] text-muted' : 'border-white/16 bg-white/[0.06] font-medium text-white',
              )}
            >
              {p.kind === 'noreply' && <IcClock size={13} />}
              <span className="text-center">{p.label}</span>
              {runCount(p.id)}
              {port(p, 'row')}
            </div>
          ))
        )}
        {stat?.messages && (
          <div className="mt-2 text-[11.5px] text-muted">
            {n(stat.messages.sent)} sent
            {stat.messages.read !== null ? ` · ${n(stat.messages.read)} read` : ''}
            {stat.messages.replied !== null ? ` · ${n(stat.messages.replied)} replied` : ''} · {n(stat.messages.skipped)} skipped
          </div>
        )}
      </>
    );
  } else if (node.type === 'update') {
    summary = updateLabel(node.data);
    body = (
      <>
        <div className="inline-block rounded-[7px] border border-brand-b/38 bg-brand-a/18 px-2 py-1 font-mono text-[12px] text-[#D8D0FF]">{summary}</div>
        <div className="mt-2 text-[11.5px] text-muted">Written to the profile in Core</div>
        {port(ports[0], 'edge')}
      </>
    );
  } else if (node.type === 'goal') {
    summary = goalLabel(node.data.outcomeTypeId, data.outcomeTypes);
    body = (
      <>
        <div className="text-[14px] font-semibold text-white">{summary}</div>
        <div className="mt-2 text-[11.5px] text-muted">Within {node.data.withinDays} days of entering · exits here</div>
      </>
    );
  } else if (node.type === 'exit') {
    summary = node.data.note ?? 'Leaves the flow';
    body = (
      <>
        <div className="text-[14px] font-semibold text-white">{summary}</div>
        <div className="mt-2 text-[11.5px] text-muted">
          {node.data.cooloffDays === 0 ? 'Can re-enter straight away' : `Can re-enter after ${node.data.cooloffDays} days`}
        </div>
      </>
    );
  } else if (node.type === 'comment') {
    summary = node.data.text;
    body = <p className="m-0 whitespace-pre-wrap text-[13px] text-muted">{node.data.text || 'Empty note'}</p>;
  }

  return (
    <div
      {...rootHandlers}
      className={cn(
        'absolute z-[2] cursor-pointer rounded-[14px] border px-3.5 pb-3.5 pt-2.5 shadow-[0_16px_34px_-20px_rgba(0,0,0,.9)] transition-[border-color,box-shadow,opacity]',
        node.type === 'comment' ? 'border-dashed border-warn/35 bg-warn/9' : 'border-line-strong bg-surface-2 hover:border-white/28',
        node.type === 'goal' && 'border-good/38',
        node.type === 'exit' && 'border-bad/32',
        sug && 'border-[1.5px] border-dashed border-[#B08CFF] bg-brand-a/8',
        selected && 'border-brand-b shadow-[0_0_0_3px_rgba(155,94,246,.3),0_16px_34px_-20px_rgba(0,0,0,.9)]',
        test === 'path' && 'border-brand-b/60',
        test === 'current' && 'border-brand-b shadow-[0_0_0_4px_rgba(155,94,246,.35),0_0_30px_rgba(155,94,246,.25)]',
        stateCls,
      )}
      style={{ left: node.position.x, top: node.position.y, width: CARD_W[node.type] ?? 240 }}
    >
      {sugBar}
      {node.type !== 'trigger' && node.type !== 'comment' && <span data-in={node.id} className="pointer-events-none absolute left-0 top-6 size-0.5" />}
      <button
        type="button"
        data-head=""
        onClick={(event) => {
          event.stopPropagation();
          select();
        }}
        aria-pressed={selected}
        aria-label={`${name}: ${summary}. ${editable ? 'Configure' : 'Details'}`}
        className={cn(
          'flex w-full cursor-pointer items-center gap-[7px] rounded-md border-0 bg-transparent pb-2 pt-0.5 text-left text-[13.5px] font-semibold',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-b',
          node.type === 'message' && 'justify-center',
          nodeTone(node),
        )}
      >
        <NodeIcon node={node} size={15} />
        <span>{name}</span>
        {headExtra}
      </button>
      {reason}
      {body}
      {stat && node.type !== 'comment' && <CardStats stat={stat} terminal={node.type === 'goal' || node.type === 'exit'} />}
    </div>
  );
}

function OkDot() {
  return (
    <span aria-hidden="true" className="grid size-[15px] flex-none place-items-center rounded-full bg-good text-[#06140D]">
      <IcCheck size={10} />
    </span>
  );
}

export const NodeView = memo(NodeViewInner);
